/**
 * Import Monitor — ERP Balance Mismatch tab.
 *
 * Every schedule slot where Helix's balance (from GRN attribution) differs
 * from the ERP's own open qty for that delivery date. Open slots by default;
 * cancelled slots can be added back. Direction reads from Helix's side.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  erpBalanceMismatchAPI,
  type ErpBalanceMismatchDirection,
  type ErpBalanceMismatchFilters,
  type ErpBalanceMismatchResponse,
  type ErpBalanceMismatchWindow,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Clock, Download, Loader2, RefreshCw, Search, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import ErpMismatchRepairModal from './ErpMismatchRepairModal';

interface ImportErpBalanceMismatchTabProps {
  customerId: number;
}

const PAGE_SIZE = 25;

export default function ImportErpBalanceMismatchTab({ customerId }: ImportErpBalanceMismatchTabProps) {
  const { formatDate, formatNumber } = useFormatters();
  const { user } = useAuth();
  const { isDashboardTabEnabled, isFeatureEnabled } = useMenuSettings();
  // Same rule as GRN Repair (server enforces it too): CR-6274 module switch,
  // Scheduled install, then superuser or staff.
  const canRepair = isFeatureEnabled('grn_repair')
    && isDashboardTabEnabled('scheduled')
    && (!!user?.is_superuser || !!user?.is_staff);
  const [repairTarget, setRepairTarget] = useState<{ poLineId: number; label: string } | null>(null);
  const [data, setData] = useState<ErpBalanceMismatchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState<ErpBalanceMismatchDirection | ''>('');
  const [dateWindow, setDateWindow] = useState<ErpBalanceMismatchWindow | ''>('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [page, setPage] = useState(1);

  const filters: ErpBalanceMismatchFilters = {
    search: search || undefined,
    direction: direction || undefined,
    window: dateWindow || undefined,
    includeCancelled,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await erpBalanceMismatchAPI.list(customerId, {
        search: search || undefined,
        direction: direction || undefined,
        window: dateWindow || undefined,
        includeCancelled,
      }, page, PAGE_SIZE));
    } catch (err: unknown) {
      console.error('ERP balance mismatch load failed:', err);
      setError('Failed to load ERP balance mismatches');
      toast.error('Failed to load ERP balance mismatches');
    } finally {
      setLoading(false);
    }
  }, [customerId, search, direction, dateWindow, includeCancelled, page]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await erpBalanceMismatchAPI.exportXlsx(customerId, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `erp_balance_mismatch_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (err: unknown) {
      console.error('ERP balance mismatch export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const summary = data?.summary;

  if (data && !data.enabled) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        ERP balance qty isn&apos;t captured for this customer&apos;s import, so there is nothing to compare.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary strip — counts across the current filters, before the direction split */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Mismatched slots</p>
            <p className="text-2xl font-semibold tabular-nums">{summary.total.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Helix balance higher</p>
            <p className="text-2xl font-semibold tabular-nums text-orange-600">{summary.helix_higher.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">ERP has booked more received</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">ERP balance higher</p>
            <p className="text-2xl font-semibold tabular-nums text-blue-600">{summary.erp_higher.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Helix has booked more received</p>
          </CardContent></Card>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64"
            placeholder="PO, PO/line or part…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); }
            }}
          />
        </div>
        <select
          className="text-sm border rounded-md py-2 px-2"
          aria-label="Direction"
          value={direction}
          onChange={(e) => { setDirection(e.target.value as ErpBalanceMismatchDirection | ''); setPage(1); }}
        >
          <option value="">Both directions</option>
          <option value="helix_higher">Helix balance higher</option>
          <option value="erp_higher">ERP balance higher</option>
        </select>
        <select
          className="text-sm border rounded-md py-2 px-2"
          aria-label="Schedule date"
          value={dateWindow}
          onChange={(e) => { setDateWindow(e.target.value as ErpBalanceMismatchWindow | ''); setPage(1); }}
        >
          <option value="">All dates</option>
          <option value="past_due">Past due</option>
          <option value="this_week">This week</option>
          <option value="future">Future</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => { setIncludeCancelled(e.target.checked); setPage(1); }}
          />
          Include cancelled
        </label>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} aria-label="Refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || !data || data.count === 0} className="gap-2">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || data.results.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No slots where the Helix balance differs from the ERP balance — all clear.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-left">
                    <th className="py-2 px-3 font-medium">PO</th>
                    <th className="py-2 px-3 font-medium">Line</th>
                    <th className="py-2 px-3 font-medium">Sched</th>
                    <th className="py-2 px-3 font-medium">Part</th>
                    <th className="py-2 px-3 font-medium">Supplier</th>
                    <th className="py-2 px-3 font-medium">Sched Date</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    <th className="py-2 px-3 font-medium text-right">Sched Qty</th>
                    <th className="py-2 px-3 font-medium text-right">Helix Received</th>
                    <th className="py-2 px-3 font-medium text-right">Helix Balance</th>
                    <th className="py-2 px-3 font-medium text-right">ERP Balance Qty</th>
                    <th className="py-2 px-3 font-medium text-right">Difference</th>
                    {canRepair && <th className="py-2 px-3 font-medium">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r) => (
                    <tr key={r.schedule_id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="py-2 px-3 font-medium">{r.po_number}</td>
                      <td className="py-2 px-3">{r.line_number}</td>
                      <td className="py-2 px-3">{r.schedule_line_number ?? '—'}</td>
                      <td className="py-2 px-3">{r.part_number ?? '—'}</td>
                      <td className="py-2 px-3">{r.supplier_name ?? '—'}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{r.schedule_date ? formatDate(r.schedule_date) : '—'}</td>
                      <td className="py-2 px-3 capitalize">{r.status.replace(/_/g, ' ')}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{formatNumber(r.schedule_qty)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{formatNumber(r.shipped_qty)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{formatNumber(r.balance_qty)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatNumber(r.erp_balance_qty)}
                        {r.awaiting_erp && (
                          <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800 whitespace-nowrap"
                            title="A receipt reached Helix after the ERP last reported this line — the ERP balance may not include it yet">
                            <Clock className="h-3 w-3" />Awaiting ERP
                          </span>
                        )}
                      </td>
                      <td className={`py-2 px-3 text-right tabular-nums font-medium ${r.difference > 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                        {r.difference > 0 ? '+' : ''}{formatNumber(r.difference)}
                      </td>
                      {canRepair && (
                        <td className="py-2 px-3">
                          <Button variant="outline" size="sm" className="whitespace-nowrap gap-1"
                            onClick={() => setRepairTarget({ poLineId: r.po_line_id, label: `${r.po_number}/${r.line_number}` })}>
                            <Wrench className="h-3.5 w-3.5" />Repair
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ErpMismatchRepairModal
        poLineId={repairTarget?.poLineId ?? null}
        label={repairTarget?.label ?? ''}
        onClose={() => setRepairTarget(null)}
        onChanged={load}
      />

      {/* Pagination */}
      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {totalPages} · {data.count.toLocaleString()} mismatched slots
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
